import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Map, { Marker } from 'react-map-gl/maplibre';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import './ForecastMap.css';
import { ForecastMapProvider, MapInteractionProvider, mapAlive } from './ForecastMapContext';
import MapActivityIndicator from './MapActivityIndicator';
import BasemapSwitcher, { savedBasemap } from './BasemapSwitcher';
import TerrainReliefLayer from './TerrainReliefLayer';
import useMediaQuery from '../../hooks/useMediaQuery';
import { spriteUrl, glyphsUrl } from '../../lib/overlays/urls';

// MapLibre normally spawns its worker from a Blob built by stringifying its own
// bundled code. CRA's babel pass over node_modules injects @babel/runtime helper
// references into that code, so the stringified worker references helpers that
// don't exist inside the Blob ("ReferenceError: v is not defined" after terser
// minification) — the worker dies, killing every worker-dependent feature in
// production builds only: vector-tile overlays, geojson/symbol layers (wind
// barbs), raster-dem hillshade (Terrain basemap), and the map never reaches
// idle. The worker must bypass webpack entirely (importing it as an asset gets
// it transpiled to CommonJS — "require is not defined"), so the stock worker
// file is copied verbatim into public/ by the sync-maplibre-worker script
// (prestart/prebuild keep it version-locked to the installed package).
// (guarded: the jsdom test mock of maplibre-gl has no setWorkerUrl)
if (typeof maplibregl.setWorkerUrl === 'function') {
    maplibregl.setWorkerUrl(`${process.env.PUBLIC_URL}/maplibre-gl-csp-worker.js`);
}

// Selected-point marker: a paraglider pin (public/assets/images/paraglider-pin.png,
// trimmed to its content so the teardrop tip sits at the bottom → anchor="bottom").
// Native trimmed size is 272×359 (aspect ≈0.758); rendered ~34×45 with a soft
// shadow + drop-in animation (see ForecastMap.css).
const PinMarker = () => (
    <img
        className="fc-marker"
        src={`${process.env.PUBLIC_URL}/assets/images/paraglider-pin.png`}
        width={34}
        height={45}
        alt=""
        aria-hidden="true"
        draggable={false}
    />
);

const DEFAULT_CENTER = { longitude: 20.9, latitude: 44.0 }; // rough Serbia center
const OVERVIEW_ZOOM = 7;
const PIN_ZOOM = 12;

// A minimal MapLibre style: OpenTopoMap raster basemap (same look as the old
// Leaflet map) + sprite/glyphs pointing at the proxied Skysight assets so the
// launch-sites layer's barb icons and labels resolve.
// Three switchable raster basemaps (BasemapSwitcher toggles layer visibility;
// the saved choice is applied at style construction so there's no flash).
// NB: Esri tile URLs are {z}/{y}/{x} — y before x.
const basemapVisibility = (id) => ({ visibility: savedBasemap() === id ? 'visible' : 'none' });
const baseStyle = {
    version: 8,
    sprite: spriteUrl(),
    glyphs: glyphsUrl(),
    sources: {
        opentopo: {
            type: 'raster',
            tiles: ['https://tile.opentopomap.org/{z}/{x}/{y}.png'],
            tileSize: 256,
            maxzoom: 17,
            attribution:
                'Map data: © OpenStreetMap contributors, SRTM | © OpenTopoMap (CC-BY-SA)',
        },
        // terrain-RGB DEM for the hillshade Terrain basemap (terrarium encoding,
        // free AWS Open Data tiles — no key required).
        terrarium: {
            type: 'raster-dem',
            tiles: ['https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png'],
            tileSize: 256,
            maxzoom: 13,
            encoding: 'terrarium',
            attribution: 'Terrain: Mapzen/AWS Open Data',
        },
        'esri-sat': {
            type: 'raster',
            tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'],
            tileSize: 256,
            maxzoom: 19,
            attribution: 'Imagery © Esri, Maxar, Earthstar Geographics',
        },
    },
    layers: [
        { id: 'basemap-topo', type: 'raster', source: 'opentopo', layout: basemapVisibility('topo') },
        { id: 'basemap-sat', type: 'raster', source: 'esri-sat', layout: basemapVisibility('sat') },
        // Terrain = warm tan ground + DEM hillshade with brown shadows —
        // matches Skysight's orange-tinted relief look.
        {
            id: 'basemap-terrain-bg', type: 'background', layout: basemapVisibility('terrain'),
            paint: { 'background-color': '#ece1cd' },
        },
        {
            id: 'basemap-terrain', type: 'hillshade', source: 'terrarium', layout: basemapVisibility('terrain'),
            paint: {
                'hillshade-shadow-color': '#8a5a3c',
                'hillshade-highlight-color': '#fff8ec',
                'hillshade-accent-color': '#c08552',
                'hillshade-exaggeration': 0.6,
            },
        },
        // Relief = hypsometric colours (TerrainReliefLayer renders them under
        // this hillshade) — separate basemap option, like Skysight's
        // "Terrain Relief" next to its plain terrain.
        {
            id: 'basemap-relief-bg', type: 'background', layout: basemapVisibility('relief'),
            paint: { 'background-color': '#dce8f2' },
        },
        {
            id: 'basemap-relief', type: 'hillshade', source: 'terrarium', layout: basemapVisibility('relief'),
            paint: {
                'hillshade-shadow-color': '#4a4036',
                'hillshade-highlight-color': '#ffffff',
                'hillshade-exaggeration': 0.5,
            },
        },
    ],
};

const ForecastMap = ({
    pin = null,
    onLocationPick = () => {},
    overlayActive = false,
    children = null,
}) => {
    const [mapInstance, setMapInstance] = useState(null);
    const [basemap, setBasemap] = useState(savedBasemap);
    const coarsePointer = useMediaQuery('(pointer: coarse)');
    const [hint, setHint] = useState(null);       // transient toast text (or null)
    const hintTimerRef = useRef(null);
    const peekClaimRef = useRef(0);               // ts of the last value shown on tap
    const lastNudgeRef = useRef(0);               // ts of the last empty-tap nudge (throttle)
    const longPressFiredRef = useRef(false);      // suppress the trailing click after a long-press

    const showHint = useCallback((text) => {
        setHint(text);
        clearTimeout(hintTimerRef.current);
        hintTimerRef.current = setTimeout(() => setHint(null), 3500);
    }, []);
    const claimPeek = useCallback(() => { peekClaimRef.current = Date.now(); }, []);
    useEffect(() => () => clearTimeout(hintTimerRef.current), []);
    const interaction = useMemo(
        () => ({ coarsePointer, showHint, claimPeek }),
        [coarsePointer, showHint, claimPeek],
    );
    // react-snap's bundled (old) Chromium can't run MapLibre's blob worker —
    // the resulting pageerror fails the Vercel build. Under the crawler we
    // render the container without the map (same guard pattern as
    // TakeOffSiteBox); react-snap's `exclude` doesn't stop link-crawling.
    const isSnap = navigator.userAgent === 'ReactSnap';
    const mapRef = useRef(null);
    // Set when the next pin change originates from a direct map click, so the
    // recenter effect can tell "user clicked here" from "navigated from a list".
    const pinFromClickRef = useRef(false);
    // Coords we last recentered on. `pin` is a fresh object every ForecastPage
    // render, so the effect must compare values — not identity — or it re-fires
    // (and re-zooms) on unrelated re-renders after the flag has been consumed.
    const lastPinRef = useRef(null);

    const initialViewState = useMemo(
        () => ({
            longitude: pin ? pin.lng : DEFAULT_CENTER.longitude,
            latitude: pin ? pin.lat : DEFAULT_CENTER.latitude,
            zoom: pin ? PIN_ZOOM : OVERVIEW_ZOOM,
        }),
        // Read once on mount; later pin changes flow through the effect below.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    // Recenter when the pin changes: zoom in if currently wider than detail,
    // else just pan (preserves the user's zoom when hopping between pins).
    // Exception: a direct map click while an overlay is on keeps the current
    // view untouched — the user is reading the overview and clicked a spot they
    // can already see, so yanking the zoom would defeat the purpose.
    useEffect(() => {
        const map = mapInstance;
        if (!pin) { lastPinRef.current = null; return; }
        if (!map) return;
        // Only react when the coordinates actually change — ignore the new pin
        // object handed to us on every unrelated re-render.
        const prev = lastPinRef.current;
        if (prev && prev.lat === pin.lat && prev.lng === pin.lng) return;
        lastPinRef.current = { lat: pin.lat, lng: pin.lng };
        const fromClick = pinFromClickRef.current;
        pinFromClickRef.current = false;
        if (fromClick && overlayActive) return;
        const target = [pin.lng, pin.lat];
        if (map.getZoom() < PIN_ZOOM) map.flyTo({ center: target, zoom: PIN_ZOOM });
        else map.panTo(target);
    }, [pin, mapInstance, overlayActive]);

    // Keep the latest pin readable from the resize handler without re-subscribing.
    const pinRef = useRef(pin);
    useEffect(() => { pinRef.current = pin; }, [pin]);

    // MapLibre's resize() anchors the top-left corner, so a centered pin slides
    // off when the container shrinks (e.g. the right panel opens and the map
    // narrows). Re-center on the current pin on every resize so the selected
    // point stays centered — this is what Leaflet's invalidateSize used to do.
    const handleResize = useCallback(() => {
        const map = mapRef.current && mapRef.current.getMap();
        const p = pinRef.current;
        if (map && p) map.setCenter([p.lng, p.lat]);
    }, []);

    const handleClick = useCallback(
        (e) => {
            if (coarsePointer) {
                // Touch: a tap never moves the pin (long-press does). The hover
                // components peek the value; if none was shown within 300ms,
                // nudge the user toward long-press (throttled).
                if (longPressFiredRef.current) { longPressFiredRef.current = false; return; }
                const tapAt = Date.now();
                setTimeout(() => {
                    if (peekClaimRef.current >= tapAt - 50) return;          // a value was peeked
                    if (Date.now() - lastNudgeRef.current < 2000) return;    // throttle
                    lastNudgeRef.current = Date.now();
                    showHint('Long-press to set location');
                }, 300);
                return;
            }
            pinFromClickRef.current = true;
            onLocationPick({ lat: e.lngLat.lat, lng: e.lngLat.lng });
        },
        [coarsePointer, onLocationPick, showHint],
    );

    // Touch: long-press (hold ~400ms, <10px move) sets the location pin. A drag
    // pans (cancels), a quick tap peeks (handled elsewhere). Coarse-pointer only.
    useEffect(() => {
        const map = mapInstance;
        if (!map || !coarsePointer) return undefined;
        let timer = null;
        let start = null;
        const onStart = (e) => {
            clearTimeout(timer);
            if (e.points && e.points.length !== 1) { start = null; return; }
            start = { x: e.point.x, y: e.point.y, lngLat: e.lngLat };
            timer = setTimeout(() => {
                if (!start) return;
                pinFromClickRef.current = true;
                longPressFiredRef.current = true;
                // The trailing `click` usually resets this; auto-clear in case it
                // never fires (e.g. iOS suppressing the synthetic click) so it
                // can't swallow a later tap's nudge.
                setTimeout(() => { longPressFiredRef.current = false; }, 700);
                if (navigator.vibrate) navigator.vibrate(15);
                onLocationPick({ lat: start.lngLat.lat, lng: start.lngLat.lng });
            }, 400);
        };
        const onMoveT = (e) => {
            if (!start) return;
            const dx = e.point.x - start.x, dy = e.point.y - start.y;
            if (dx * dx + dy * dy > 100) clearTimeout(timer); // moved >10px → pan, not a press
        };
        const onEnd = () => { clearTimeout(timer); start = null; };
        map.on('touchstart', onStart);
        map.on('touchmove', onMoveT);
        map.on('touchend', onEnd);
        map.on('touchcancel', onEnd);
        return () => {
            clearTimeout(timer);
            if (!mapAlive(map)) return;
            map.off('touchstart', onStart);
            map.off('touchmove', onMoveT);
            map.off('touchend', onEnd);
            map.off('touchcancel', onEnd);
        };
    }, [mapInstance, coarsePointer, onLocationPick]);

    // First-run hint on mobile (shown once, remembered).
    useEffect(() => {
        if (!coarsePointer) return;
        try {
            if (localStorage.getItem('fc-mobile-pin-hint')) return;
            localStorage.setItem('fc-mobile-pin-hint', '1');
        } catch (_) { return; }
        showHint('Long-press the map to set a location');
    }, [coarsePointer, showHint]);

    const handleLoad = useCallback(() => {
        setMapInstance(mapRef.current && mapRef.current.getMap());
    }, []);

    return (
        <div className="w-full h-full relative isolate" data-testid="forecast-map">
            {!isSnap && <Map
                ref={mapRef}
                mapLib={maplibregl}
                initialViewState={initialViewState}
                mapStyle={baseStyle}
                onClick={handleClick}
                onLoad={handleLoad}
                onResize={handleResize}
                style={{ width: '100%', height: '100%' }}
            >
                {pin && (
                    <Marker longitude={pin.lng} latitude={pin.lat} anchor="bottom">
                        <PinMarker />
                    </Marker>
                )}
                <ForecastMapProvider value={mapInstance}>
                    <MapInteractionProvider value={interaction}>
                        <MapActivityIndicator />
                        <BasemapSwitcher active={basemap} onChange={setBasemap} />
                        {/* hypsometric colour relief under the hillshade (Terrain) */}
                        {basemap === 'relief' && <TerrainReliefLayer />}
                        {children}
                    </MapInteractionProvider>
                </ForecastMapProvider>
            </Map>}
            {hint && (
                <div
                    className="pointer-events-none absolute bottom-4 left-1/2 -translate-x-1/2 z-[7] px-3 py-1.5 rounded-full bg-slate-900/85 text-white text-xs font-medium shadow-lg"
                    style={{ textShadow: '0 1px 2px rgba(0, 0, 0, 0.9)' }}
                >
                    {hint}
                </div>
            )}
        </div>
    );
};

export default ForecastMap;
